/**
 * Phone + INN helpers (ported from cpanel
 * `features/merchants/model/constants.ts` — logic 1:1).
 */

/** E.164 with a leading `+` — the API rejects anything else. */
export function toE164(phone: string | undefined | null): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export const COMPANY_INN_LENGTH = 9;
export const INDIVIDUAL_PINFL_LENGTH = 14;
export const INN_LENGTHS = [COMPANY_INN_LENGTH, INDIVIDUAL_PINFL_LENGTH] as const;

export function innLengthsFor(legalForm?: string): readonly number[] {
  return legalForm === 'LEGAL_FORM_INDIVIDUAL'
    ? [INDIVIDUAL_PINFL_LENGTH]
    : [COMPANY_INN_LENGTH];
}

export function innLabelFor(): string {
  return 'ИНН/ПИНФЛ';
}

export function isValidInn(inn: string | undefined, legalForm?: string): boolean {
  const raw = inn ?? '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== raw.length) return false;
  return innLengthsFor(legalForm).includes(digits.length);
}

export function isIndividualLegalForm(legalForm?: string): boolean {
  return legalForm === 'LEGAL_FORM_INDIVIDUAL';
}

/** i18n key for the INN/PINFL hint — call sites render `t(innHintKeyFor(...))`. */
export function innHintKeyFor(legalForm?: string): string {
  return isIndividualLegalForm(legalForm)
    ? 'merchants:inn.hintIndividual'
    : 'merchants:inn.hintCompany';
}

/** Canonical merchant id: `merchant_id` first, `id` as fallback. */
export function getMerchantId(
  merchant:
    | { merchant_id?: string | undefined; id?: string | undefined }
    | null
    | undefined,
): string | undefined {
  const id = merchant?.merchant_id ?? merchant?.id;
  if (id === undefined || id === '') return undefined;
  return id;
}

/** Display fallback: dash for missing OR empty strings (cpanel `|| '—'` parity). */
export function orDash(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  return value;
}

/** Drop the UNSPECIFIED default — it is not a real selection. */
export function getSelectedVerticals(
  merchant: { verticals?: string[] | undefined } | null | undefined,
): string[] {
  return (merchant?.verticals ?? []).filter((v) => v !== 'MERCHANT_VERTICAL_UNSPECIFIED');
}

/** Delivery tab sorts first in the integration tab strip. */
export function getIntegrationTabs(verticals: string[]): string[] {
  const delivery = verticals.filter((v) => v === 'MERCHANT_VERTICAL_DELIVERY');
  return [...delivery, ...verticals.filter((v) => v !== 'MERCHANT_VERTICAL_DELIVERY')];
}

/**
 * Every service tab the merchant dialog shows, regardless of the merchant's
 * own verticals: delivery first, then the rest in `VERTICAL_OPTIONS` order.
 * A tab whose integration API answers 404 renders the restricted state
 * instead of its form — the tab strip itself never hides.
 */
export const ALL_MERCHANT_VERTICALS = [
  'MERCHANT_VERTICAL_EATS',
  'MERCHANT_VERTICAL_PHARMA',
  'MERCHANT_VERTICAL_DELIVERY',
] as const;

export function getAllIntegrationTabs(): string[] {
  return getIntegrationTabs([...ALL_MERCHANT_VERTICALS]);
}

/**
 * Edit flow restarts the delivery verification used on create when the saved
 * merchant keeps/offers the delivery vertical.
 */
export function shouldRestartDeliverySync(verticals?: string[] | null): boolean {
  return (verticals ?? []).includes('MERCHANT_VERTICAL_DELIVERY');
}

const DASH = '—';

export function formatDateTime(value?: string | null): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
